/**
 * Emits container app focus on `BroadcastChannel('notifications')` so the
 * notifications MFE can zero visit-aware badge counts for the active app.
 */
import { useEffect, useRef } from 'react'

import { BroadcastChannels } from 'shared/constants/broadcast/channels'
import {
  HEARTBEAT_INTERVAL_MS,
  NotificationsBroadcastMessages
} from 'shared/constants/broadcast/notifications'
import { broadcastAppFocusChange } from 'shared/utils/broadcastAppFocusChange'
import { broadcastAppFocusHeartbeat } from 'shared/utils/broadcastAppFocusHeartbeat'
import { createTypedBroadcastChannel } from 'shared/utils/broadcastChannel'

import {
  isAppFocusStateReady,
  resolveInTabActualFocusedApp,
  shouldSkipAppFocusTracking
} from './appFocusLogic'
import { useAppFocusContext } from './useAppFocusContext'

export const AppFocusBroadcaster = (): null => {
  const focusContext = useAppFocusContext()
  const focusContextRef = useRef(focusContext)
  focusContextRef.current = focusContext

  const inTabActualFocusedAppRef = useRef<string | null | undefined>(undefined)
  const focusedAppIdRef = useRef<string | null | undefined>(undefined)
  const isTabVisibleRef = useRef(document.visibilityState === 'visible')
  const hasAnnouncedInitialRef = useRef(false)
  const pendingDetachedFocusRef = useRef<string | null | undefined>(undefined)

  // Ref-only closure — safe to capture in mount-only effects; do not add props/state here.
  const publishFocusedApp = (to: string | null, force = false) => {
    const from = focusedAppIdRef.current ?? null
    if (!force && from === to) return

    focusedAppIdRef.current = to
    broadcastAppFocusChange(from, to)
  }

  const clearPendingDetachedFocus = () => {
    pendingDetachedFocusRef.current = undefined
  }

  const shouldIgnoreDetachedInTabChange = (
    nextInTab: string | null | undefined
  ): boolean => {
    const pending = pendingDetachedFocusRef.current
    if (pending === undefined) return false

    if (nextInTab === pending) return true

    // Ignore transient null flicker while a detached destination is settling.
    if (pending !== null && (nextInTab === null || nextInTab === undefined)) {
      return true
    }

    return false
  }

  useEffect(() => {
    const context = focusContext

    if (!hasAnnouncedInitialRef.current && !isAppFocusStateReady(context)) {
      return
    }

    const nextInTab = resolveInTabActualFocusedApp(context)
    if (nextInTab === undefined) return

    const prevInTab = inTabActualFocusedAppRef.current
    const inTabChanged = prevInTab !== nextInTab
    inTabActualFocusedAppRef.current = nextInTab

    if (!isTabVisibleRef.current) return

    if (!hasAnnouncedInitialRef.current) {
      hasAnnouncedInitialRef.current = true

      if (context.isDetachedRoute) {
        pendingDetachedFocusRef.current = nextInTab
        return
      }

      publishFocusedApp(nextInTab, true)
      return
    }

    if (!inTabChanged) return

    if (context.isDetachedRoute) {
      if (shouldIgnoreDetachedInTabChange(nextInTab)) return

      const currentFocused = focusedAppIdRef.current ?? null
      if (currentFocused !== null) {
        publishFocusedApp(null, true)
      }

      pendingDetachedFocusRef.current = nextInTab
      return
    }

    publishFocusedApp(nextInTab, true)
  }, [focusContext])

  useEffect(() => {
    const pending = pendingDetachedFocusRef.current
    if (pending === undefined) return

    const context = focusContext

    if (!context.isDetachedRoute) {
      clearPendingDetachedFocus()
      return
    }

    if (!isTabVisibleRef.current) return

    const { appId, status } = context.detachedAppLoadState
    if (appId !== pending) return
    if (status === 'loading' || status === 'idle') return

    if (status === 'ready') {
      publishFocusedApp(pending)
    } else {
      publishFocusedApp(null, true)
    }

    clearPendingDetachedFocus()
  }, [focusContext])

  useEffect(() => {
    const onVisibilityChange = () => {
      const context = focusContextRef.current

      if (shouldSkipAppFocusTracking(context)) return

      const isTabVisible = document.visibilityState === 'visible'
      isTabVisibleRef.current = isTabVisible

      if (!isTabVisible) {
        clearPendingDetachedFocus()
        publishFocusedApp(null, true)
        return
      }

      if (!isAppFocusStateReady(context)) return

      const nextInTab = resolveInTabActualFocusedApp(context)
      if (nextInTab === undefined) return

      inTabActualFocusedAppRef.current = nextInTab
      // Tab visible again: destination is already rendered — skip detached load wait.
      publishFocusedApp(nextInTab, true)
    }

    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange)
      clearPendingDetachedFocus()
      // Unmount defocus — avoid leaving consumers stuck on phantom focus.
      if (focusedAppIdRef.current != null) {
        publishFocusedApp(null, true)
      }
    }
  }, [])

  useEffect(() => {
    const intervalId = setInterval(() => {
      if (isTabVisibleRef.current && focusedAppIdRef.current != null) {
        broadcastAppFocusHeartbeat(focusedAppIdRef.current)
      }
    }, HEARTBEAT_INTERVAL_MS)

    return () => clearInterval(intervalId)
  }, [])

  useEffect(() => {
    const channel = createTypedBroadcastChannel(BroadcastChannels.NOTIFICATIONS)
    const unsubscribe = channel.subscribe(message => {
      if (
        message.type !== NotificationsBroadcastMessages.APP_FOCUS_SYNC_REQUEST
      ) {
        return
      }

      if (isTabVisibleRef.current && focusedAppIdRef.current != null) {
        broadcastAppFocusHeartbeat(focusedAppIdRef.current)
      }
    })

    return () => {
      unsubscribe()
      channel.close()
    }
  }, [])

  return null
}
